import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { isEmpty } from 'lodash-es';
import { getFullName } from '@frontend/name';
import { ScheduleRequestListItem, usePreferenceBasedRequestsAccess } from '@frontend/schedule-helpers';
import { useModalControl } from '@frontend/design-system';
import { useCheckPaymentsEnabled } from '../../../../../../../hooks/use-check-payments-enabled';
import type { ScheduleRequestActionCalledFrom } from '../../hooks/use-schedule-request-actions';
import { PreferenceBasedScheduleRequestItemHeader } from './preference-based-item-header';
import { ScheduleRequestListItemHeader } from './schedule-request-item-header';

export type ScheduleRequestItemHeaderProps = {
  isWidgetListItem?: boolean;
  listItem: ScheduleRequestListItem;
  setSelectedScheduleRequest: (request: ScheduleRequest | null) => void;
  scheduleRequestModalProps: ReturnType<typeof useModalControl>;
  locationChip?: (locationName: string) => React.ReactNode;
  patientContactedAt?: string;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  rejectRequestModalProps: ReturnType<typeof useModalControl>;
  onCompleteRequest: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onDismissRequestIntent: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
};

export const ScheduleRequestItemHeader = ({
  isWidgetListItem,
  listItem,
  setSelectedScheduleRequest,
  scheduleRequestModalProps,
  locationChip,
  patientContactedAt,
  deleteRequestModalProps,
  dismissRequestModalProps,
  rejectRequestModalProps,
  onCompleteRequest,
  onDismissRequestIntent,
  onMessage,
  onPhoneCall,
}: ScheduleRequestItemHeaderProps) => {
  const { isPaymentsEnabled } = useCheckPaymentsEnabled(listItem.locationId);
  const { hasPreferenceBasedRequestsAccess } = usePreferenceBasedRequestsAccess();
  const isPreferenceBasedRequest = !isEmpty(listItem.aiRequestDetails);
  const isAiRequest = hasPreferenceBasedRequestsAccess && isPreferenceBasedRequest;
  const isPartnerRequest = listItem.attribution?.platform === 'PARTNER';
  const isUrgentRequest =
    (isAiRequest && listItem.aiRequestDetails?.visitDetails?.priority === 'urgent') ||
    (isPartnerRequest && listItem?.priority === 'URGENT');

  const sharedProps = {
    requestVersion: listItem.linkHash || '',
    name: getFullName(listItem.schedulee ?? {}),
    appointmentTime: listItem?.dateTime ?? '',
    personPhone: listItem?.schedulee?.phoneNumber ?? '',
    locationId: listItem.locationId ?? '',
    scheduleRequestId: listItem.id ?? '',
    isWidgetListItem,
    locationChip,
  };

  const updateSelectedScheduleRequest = () => {
    if (isAiRequest) {
      // TODO: Handle widget action
    } else {
      setSelectedScheduleRequest(listItem);
      scheduleRequestModalProps.openModal();
    }
  };

  return isPartnerRequest || isAiRequest ? (
    <PreferenceBasedScheduleRequestItemHeader
      {...sharedProps}
      isAiRequest={isAiRequest}
      isUrgentRequest={isUrgentRequest}
      patientContactedAt={patientContactedAt}
      deleteRequestModalProps={deleteRequestModalProps}
      dismissRequestModalProps={dismissRequestModalProps}
      onCompleteRequest={onCompleteRequest}
      onDismissRequestIntent={onDismissRequestIntent}
      onMessage={onMessage}
      onPhoneCall={onPhoneCall}
    />
  ) : (
    <ScheduleRequestListItemHeader
      {...sharedProps}
      paidAmount={isPaymentsEnabled && listItem?.bookingAmount ? listItem?.bookingAmount : 0}
      updateSelectedScheduleRequest={updateSelectedScheduleRequest}
      deleteRequestModalProps={deleteRequestModalProps}
      rejectRequestModalProps={rejectRequestModalProps}
      onMessage={onMessage}
    />
  );
};
